import { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint, faPlaneArrival, faPlaneDeparture, faHotel, faClipboardList, faMoneyBill, faCreditCard, faMobileScreen, faMoneyBillWave } from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";

// ── Constants ───────────────────────────────────────────────
const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";
const EGP = (n: number) => `EGP ${(n ?? 0).toLocaleString()}`;

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function fmtTime(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── Interfaces ──────────────────────────────────────────────
interface AuditArrival {
  bookingId: string;
  guestName: string;
  roomId: string;
  nights: number;
  total: number;
  status: string;
}

interface AuditDeparture {
  bookingId: string;
  guestName: string;
  roomId: string;
  nights: number;
  total: number;
  paymentStatus: string;
}

interface AuditInHouse {
  bookingId: string;
  guestName: string;
  roomId: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  paymentStatus: string;
  paid: number;
  remaining: number;
}

interface AuditNewBooking {
  bookingId: string;
  guestName: string;
  roomId: string;
  checkIn: string;
  checkOut: string;
  total: number;
  source: string;
}

interface AuditPayment {
  id: number;
  bookingId: string;
  guestName: string | null;
  amount: number;
  method: string;
  note: string;
  cardLast4: string | null;
  transactionRef: string | null;
  createdAt: string;
}

interface AuditSummary {
  arrivalsCount: number;
  departuresCount: number;
  inHouseCount: number;
  newBookingsCount: number;
  totalPaymentsCollected: number;
  cashCollected: number;    cashCount: number;
  visaCollected: number;    visaCount: number;
  instapayCollected: number; instapayCount: number;
  pendingBalance: number;
}

interface AuditData {
  date: string;
  arrivals: AuditArrival[];
  departures: AuditDeparture[];
  inHouse: AuditInHouse[];
  newBookings: AuditNewBooking[];
  payments: AuditPayment[];
  summary: AuditSummary;
}

// ── Auth headers ─────────────────────────────────────────────
const authHeaders = {
  Authorization: `Bearer ${localStorage.getItem("ai2_token")}`,
  "x-hotel": localStorage.getItem("ai2_hotel") || "",
};

// ── Badge helpers ────────────────────────────────────────────
function paymentStatusColor(status: string): string {
  switch (status?.toLowerCase()) {
    case "paid":    return "#10b981";
    case "partial": return "#f59e0b";
    default:        return "#ef4444";
  }
}

function methodColor(method: string): string {
  switch (method?.toLowerCase()) {
    case "cash":     return "#10b981";
    case "visa":
    case "card":     return "#1a3a5c";
    case "instapay": return "#a855f7";
    default:         return "#64748b";
  }
}

function StatusBadge({ label }: { label: string }) {
  const color = paymentStatusColor(label);
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 600,
      color,
      background: color + "1a",
      border: `1px solid ${color}55`,
      textTransform: "capitalize",
    }}>
      {label || "unpaid"}
    </span>
  );
}

function MethodBadge({ label }: { label: string }) {
  const color = methodColor(label);
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 600,
      color,
      background: color + "1a",
      border: `1px solid ${color}55`,
      textTransform: "capitalize",
    }}>
      {label || "—"}
    </span>
  );
}

// ── Collapsible Section ──────────────────────────────────────
function Section({
  title,
  count,
  accent,
  children,
}: {
  title: string;
  count?: number;
  accent?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  const col = accent || "#1a3a5c";
  return (
    <div style={{
      background: "#0E1E38",
      border: "1px solid #e2e8f0",
      borderRadius: 14,
      marginBottom: 20,
      overflow: "hidden",
    }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 22px",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "#F2EBD9",
          fontFamily: "inherit",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{
            width: 4, height: 22, borderRadius: 4,
            background: col, display: "inline-block", flexShrink: 0,
          }} />
          <span style={{ fontSize: 16, fontWeight: 700, color: "#F2EBD9" }}>{title}</span>
          {count !== undefined && (
            <span style={{
              fontSize: 12, fontWeight: 600, color: col,
              background: col + "1a", border: `1px solid ${col}44`,
              borderRadius: 8, padding: "1px 8px",
            }}>
              {count}
            </span>
          )}
        </div>
        <span style={{
          fontSize: 18, color: "rgba(242,235,217,0.4)",
          transform: open ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
        }}>›</span>
      </button>
      {open && (
        <div style={{ padding: "0 0 16px 0" }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── Responsive table wrapper ─────────────────────────────────
function TableWrap({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ overflowX: "auto", padding: "0 22px" }}>
      <table style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 13,
        color: "#F2EBD9",
      }}>
        {children}
      </table>
    </div>
  );
}

const Th = ({ children, right }: { children: React.ReactNode; right?: boolean }) => (
  <th style={{
    textAlign: right ? "right" : "left",
    padding: "8px 12px",
    color: "rgba(242,235,217,0.4)",
    fontWeight: 600,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    whiteSpace: "nowrap",
  }}>
    {children}
  </th>
);

const Td = ({
  children,
  right,
  muted,
}: {
  children: React.ReactNode;
  right?: boolean;
  muted?: boolean;
}) => (
  <td style={{
    padding: "11px 12px",
    textAlign: right ? "right" : "left",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    color: muted ? "rgba(242,235,217,0.5)" : "#F2EBD9",
    verticalAlign: "middle",
  }}>
    {children}
  </td>
);

function EmptyRow({ cols, msg }: { cols: number; msg?: string }) {
  return (
    <tr>
      <td colSpan={cols} style={{
        textAlign: "center",
        padding: "28px 12px",
        color: "rgba(242,235,217,0.45)",
        fontSize: 13,
      }}>
        {msg || "No records"}
      </td>
    </tr>
  );
}

const EGP_INT = (n: number) => `EGP ${Math.round(n ?? 0).toLocaleString()}`;

function methodLabel(method: string, cardLast4?: string | null): string {
  const m = method?.toLowerCase();
  if (m === "visa" || m === "card") return cardLast4 ? `Visa ****${cardLast4}` : "Visa";
  if (m === "instapay") return "Instapay";
  return "Cash";
}

function methodColor2(method: string): { color: string; bg: string; border: string } {
  const m = method?.toLowerCase();
  if (m === "visa" || m === "card") return { color: "#c9a84c", bg: "rgba(201,168,76,0.12)", border: "rgba(201,168,76,0.35)" };
  if (m === "instapay") return { color: "#a855f7", bg: "rgba(168,85,247,0.12)", border: "rgba(168,85,247,0.35)" };
  return { color: "#10b981", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.35)" };
}

function printSettlement(type: "summary" | "transactions", data: AuditData, hotelName: string) {
  const s = data.summary;
  const ps = data.payments;
  const date = fmtDate(data.date);
  const generated = new Date().toLocaleString("en-GB");
  const grandTotal = ps.reduce((a, p) => a + p.amount, 0);

  const summaryHtml = `
    <h2 style="margin:0 0 6px;font-size:15pt">Payment Settlement Summary</h2>
    <p style="margin:0 0 2px;color:#555;font-size:10pt">${hotelName} · ${date} · Generated ${generated}</p>
    <hr style="margin:10px 0;border:1pt solid #ccc"/>
    <table style="width:100%;border-collapse:collapse;font-size:11pt;margin-bottom:12px">
      <thead><tr style="background:#f1f5f9">
        <th style="text-align:left;padding:6px 10px;border-bottom:1pt solid #ccc">Method</th>
        <th style="text-align:center;padding:6px 10px;border-bottom:1pt solid #ccc">Count</th>
        <th style="text-align:right;padding:6px 10px;border-bottom:1pt solid #ccc">Total</th>
      </tr></thead>
      <tbody>
        <tr><td style="padding:7px 10px;border-bottom:1pt solid #e2e8f0">Cash</td><td style="text-align:center;padding:7px 10px;border-bottom:1pt solid #e2e8f0">${s.cashCount}</td><td style="text-align:right;padding:7px 10px;border-bottom:1pt solid #e2e8f0;font-weight:700">EGP ${Math.round(s.cashCollected).toLocaleString()}</td></tr>
        <tr><td style="padding:7px 10px;border-bottom:1pt solid #e2e8f0">Visa / Card</td><td style="text-align:center;padding:7px 10px;border-bottom:1pt solid #e2e8f0">${s.visaCount}</td><td style="text-align:right;padding:7px 10px;border-bottom:1pt solid #e2e8f0;font-weight:700">EGP ${Math.round(s.visaCollected).toLocaleString()}</td></tr>
        <tr><td style="padding:7px 10px;border-bottom:1pt solid #e2e8f0">Instapay</td><td style="text-align:center;padding:7px 10px;border-bottom:1pt solid #e2e8f0">${s.instapayCount}</td><td style="text-align:right;padding:7px 10px;border-bottom:1pt solid #e2e8f0;font-weight:700">EGP ${Math.round(s.instapayCollected).toLocaleString()}</td></tr>
        <tr style="font-weight:800;font-size:12pt"><td colspan="2" style="padding:10px 10px 4px;border-top:2pt solid #111">Grand Total</td><td style="text-align:right;padding:10px 10px 4px;border-top:2pt solid #111">EGP ${Math.round(grandTotal).toLocaleString()}</td></tr>
      </tbody>
    </table>
  `;

  const transHtml = `
    <h2 style="margin:0 0 6px;font-size:15pt">Transaction Detail Report</h2>
    <p style="margin:0 0 2px;color:#555;font-size:10pt">${hotelName} · ${date} · Generated ${generated}</p>
    <hr style="margin:10px 0;border:1pt solid #ccc"/>
    <table style="width:100%;border-collapse:collapse;font-size:10pt">
      <thead><tr style="background:#f1f5f9">
        <th style="text-align:left;padding:5px 8px;border-bottom:1pt solid #ccc">#</th>
        <th style="text-align:left;padding:5px 8px;border-bottom:1pt solid #ccc">Time</th>
        <th style="text-align:left;padding:5px 8px;border-bottom:1pt solid #ccc">Guest</th>
        <th style="text-align:right;padding:5px 8px;border-bottom:1pt solid #ccc">Amount</th>
        <th style="text-align:left;padding:5px 8px;border-bottom:1pt solid #ccc">Method</th>
        <th style="text-align:left;padding:5px 8px;border-bottom:1pt solid #ccc">Note</th>
      </tr></thead>
      <tbody>
        ${ps.map((p, i) => `
          <tr>
            <td style="padding:5px 8px;border-bottom:1pt solid #e2e8f0;color:#888">${i + 1}</td>
            <td style="padding:5px 8px;border-bottom:1pt solid #e2e8f0;color:#555">${p.createdAt ? new Date(p.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
            <td style="padding:5px 8px;border-bottom:1pt solid #e2e8f0;font-weight:600">${p.guestName || p.bookingId || "—"}</td>
            <td style="text-align:right;padding:5px 8px;border-bottom:1pt solid #e2e8f0;font-weight:700">EGP ${Math.round(p.amount).toLocaleString()}</td>
            <td style="padding:5px 8px;border-bottom:1pt solid #e2e8f0">${methodLabel(p.method, p.cardLast4)}</td>
            <td style="padding:5px 8px;border-bottom:1pt solid #e2e8f0;color:#888">${p.note || "—"}</td>
          </tr>
        `).join("")}
        <tr style="font-weight:800">
          <td colspan="3" style="padding:8px;border-top:2pt solid #111;text-align:right">Total</td>
          <td style="padding:8px;border-top:2pt solid #111;text-align:right">EGP ${Math.round(grandTotal).toLocaleString()}</td>
          <td colspan="2" style="border-top:2pt solid #111"></td>
        </tr>
      </tbody>
    </table>
  `;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Settlement ${date}</title>
    <style>body{font-family:Georgia,serif;margin:20mm 18mm;color:#111}</style>
  </head><body>
    ${type === "summary" ? summaryHtml : transHtml}
  </body></html>`;

  const w = window.open("", "_blank", "width=800,height=600");
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 300);
}

// ── Main Page ────────────────────────────────────────────────
export default function NightAuditPage() {
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState<AuditData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shiftNotes, setShiftNotes] = useState("");

  const fetchReport = async (d: string) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get<AuditData>(
        `${API_BASE}/api/dashboard/night-audit?date=${d}`,
        { headers: authHeaders }
      );
      setData(res.data);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load night audit report."
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const s = data?.summary;

  return (
    <DashboardLayout>
      {/* ── Print styles injected inline ─────────────────── */}
      <style>{`
        @media print {
          /* Hide everything except the report */
          .na-no-print,
          nav, aside, header,
          [class*="sidebar"], [class*="Sidebar"],
          [class*="layout-side"], [class*="nav-"],
          [class*="dashboard-nav"], [class*="dashboard-sidebar"],
          [class*="dash-nav"], [class*="dash-side"] {
            display: none !important;
          }
          /* Full-width white background */
          body, html { background: #fff !important; color: #111 !important; margin: 0 !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          [class*="dashboard-content"], [class*="dashboard-main"],
          [class*="dashboard-wrapper"], [class*="layout-content"],
          [class*="main-content"], main {
            margin: 0 !important; padding: 0 !important;
            width: 100% !important; max-width: 100% !important;
          }
          /* Report container */
          .na-print-wrap {
            padding: 20mm 18mm !important;
            color: #111 !important;
            font-family: Georgia, serif !important;
          }
          /* Sections */
          section, .na-print-section {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          /* Tables */
          table { width: 100%; border-collapse: collapse; font-size: 11pt; }
          th { background: #f1f5f9 !important; color: #334155 !important; font-size: 9pt; padding: 5px 8px; border-bottom: 1pt solid #cbd5e1; text-align: left; }
          td { padding: 5px 8px; border-bottom: 1pt solid #e2e8f0; color: #1e293b; }
          /* KPI strip */
          .na-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6mm; margin-bottom: 8mm; }
          .na-kpi-card { background: #f8fafc !important; border: 1pt solid #e2e8f0 !important; border-radius: 4pt; padding: 4mm 5mm; }
          .na-kpi-value { font-size: 18pt; font-weight: 700; color: #111927 !important; }
          .na-kpi-label { font-size: 8pt; color: #64748b !important; margin-top: 2pt; }
          /* Section headings */
          .na-section-head { font-size: 13pt; font-weight: 700; color: #1e293b !important; border-bottom: 2pt solid #2563eb; padding-bottom: 3mm; margin: 7mm 0 4mm; }
          /* Payment row colors to readable greys */
          span[style*="color"] { color: #334155 !important; }
          /* Page break before each major section */
          .na-page-break { page-break-before: always; }
        }
      `}</style>

      <div className="na-print-wrap" style={{ padding: "28px 28px 40px", minHeight: "100vh", color: "#F2EBD9" }}>

        {/* ── Print-only header ────────────────────────── */}
        {data && (
          <div style={{ display: "none" }} className="na-print-header">
            <style>{`
              @media print {
                .na-print-header {
                  display: flex !important;
                  align-items: flex-start;
                  justify-content: space-between;
                  border-bottom: 2pt solid #111927;
                  padding-bottom: 6mm;
                  margin-bottom: 8mm;
                }
                .na-print-header-title { font-size: 22pt; font-weight: 700; color: #111927; margin: 0; }
                .na-print-header-sub { font-size: 10pt; color: #64748b; margin: 2mm 0 0; }
                .na-print-header-right { text-align: right; font-size: 9pt; color: #475569; }
              }
            `}</style>
            <div>
              <div className="na-print-header-title">Night Audit Report</div>
              <div className="na-print-header-sub">
                {localStorage.getItem("ai2_hotel") || "Hotel"} · Report date: {fmtDate(data.date)}
              </div>
            </div>
            <div className="na-print-header-right">
              <div>Generated: {new Date().toLocaleString("en-GB")}</div>
              <div style={{ marginTop: 4 }}>Confidential — Internal Use Only</div>
            </div>
          </div>
        )}

        {/* ── Page header ──────────────────────────────── */}
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 28,
        }}>
          <div>
            <h1 style={{
              fontSize: 24, fontWeight: 800, color: "#F2EBD9",
              margin: 0, letterSpacing: "-0.5px",
            }}>
              Night Audit
            </h1>
            <p style={{ margin: "4px 0 0", color: "rgba(242,235,217,0.4)", fontSize: 14 }}>
              Shift closing report — all transactions and occupancy for a selected date.
            </p>
          </div>

          {/* Controls */}
          <div className="na-no-print" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ color: "rgba(242,235,217,0.6)", fontSize: 13, fontWeight: 500 }}>Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={{
                  background: "#0E1E38",
                  border: "1px solid rgba(201,168,76,0.25)",
                  borderRadius: 8,
                  color: "#F2EBD9",
                  padding: "8px 12px",
                  fontSize: 14,
                  outline: "none",
                  cursor: "pointer",
                }}
              />
            </div>
            <button
              onClick={() => fetchReport(date)}
              disabled={loading}
              style={{
                background: "linear-gradient(135deg, #C9A84C, #E8C97A)",
                color: "#0E1E38",
                border: "none",
                borderRadius: 8,
                padding: "8px 18px",
                fontSize: 14,
                fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                fontFamily: "inherit",
                transition: "opacity 0.2s",
              }}
            >
              {loading ? "Loading…" : "Generate Report"}
            </button>
            <button
              onClick={() => window.print()}
              style={{
                background: "rgba(201,168,76,0.12)",
                color: "#C9A84C",
                border: "1px solid rgba(201,168,76,0.3)",
                borderRadius: 8,
                padding: "8px 16px",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <FontAwesomeIcon icon={faPrint} /> Print
            </button>
          </div>
        </div>

        {/* ── Error banner ─────────────────────────────── */}
        {error && (
          <div style={{
            background: "rgba(239,68,68,0.1)",
            border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 10,
            padding: "12px 18px",
            color: "#ef4444",
            marginBottom: 24,
            fontSize: 14,
          }}>
            {error}
          </div>
        )}

        {/* ── Loading skeleton hint ─────────────────────── */}
        {loading && !data && (
          <div style={{
            textAlign: "center", padding: "60px 0",
            color: "rgba(242,235,217,0.45)", fontSize: 15,
          }}>
            Generating report…
          </div>
        )}

        {/* ── Report date label ─────────────────────────── */}
        {data && (
          <div style={{
            fontSize: 13, color: "rgba(242,235,217,0.4)", marginBottom: 20, fontWeight: 500,
          }}>
            Report for{" "}
            <span style={{ color: "#c9a84c", fontWeight: 700 }}>
              {fmtDate(data.date)}
            </span>
          </div>
        )}

        {/* ════════════════════════════════════════════════
            KPI Summary strip
        ════════════════════════════════════════════════ */}
        {s && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 14,
            marginBottom: 20,
          }}>
            {[
              { label: "Arrivals",         value: s.arrivalsCount,            color: "#C9A84C", icon: faPlaneArrival },
              { label: "Departures",        value: s.departuresCount,           color: "#f59e0b", icon: faPlaneDeparture },
              { label: "In House",          value: s.inHouseCount,              color: "#10b981", icon: faHotel },
              { label: "New Bookings",      value: s.newBookingsCount,          color: "#a855f7", icon: faClipboardList },
              { label: "Total Collected",   value: EGP(s.totalPaymentsCollected), color: "#10b981", icon: faMoneyBill },
              { label: "Pending Balance",   value: EGP(s.pendingBalance),       color: "#ef4444", icon: faCreditCard },
            ].map((k) => (
              <div key={k.label} style={{
                background: "#0E1E38",
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: "16px 18px",
              }}>
                <div style={{ fontSize: 20, marginBottom: 6 }}><FontAwesomeIcon icon={k.icon} /></div>
                <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>
                  {k.value}
                </div>
                <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", marginTop: 3, fontWeight: 500 }}>
                  {k.label}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ════════════════════════════════════════════════
            Payment Breakdown strip
        ════════════════════════════════════════════════ */}
        {s && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
            gap: 14,
            marginBottom: 28,
          }}>
            {[
              { label: "Cash Collected",     value: s.cashCollected,     color: "#10b981", icon: faMoneyBillWave },
              { label: "Visa Collected",     value: s.visaCollected,     color: "#C9A84C", icon: faCreditCard },
              { label: "Instapay Collected", value: s.instapayCollected, color: "#a855f7", icon: faMobileScreen },
            ].map((k) => (
              <div key={k.label} style={{
                background: "#0E1E38",
                border: `1px solid ${k.color}22`,
                borderRadius: 14,
                padding: "14px 18px",
                display: "flex",
                alignItems: "center",
                gap: 14,
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 10,
                  background: k.color + "22",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 20, flexShrink: 0,
                }}>
                  <FontAwesomeIcon icon={k.icon} />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: k.color }}>
                    {EGP(k.value)}
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", marginTop: 2, fontWeight: 500 }}>
                    {k.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ════════════════════════════════════════════════
            Arrivals
        ════════════════════════════════════════════════ */}
        {data && (
          <Section title="Arrivals" count={data.arrivals.length} accent="#1a3a5c">
            <TableWrap>
              <thead>
                <tr>
                  <Th>Booking ID</Th>
                  <Th>Guest</Th>
                  <Th>Room</Th>
                  <Th>Nights</Th>
                  <Th right>Total</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {data.arrivals.length === 0 ? (
                  <EmptyRow cols={6} msg="No arrivals for this date" />
                ) : (
                  data.arrivals.map((a) => (
                    <tr key={a.bookingId}>
                      <Td muted>{a.bookingId}</Td>
                      <Td>{a.guestName}</Td>
                      <Td>{a.roomId}</Td>
                      <Td muted>{a.nights}</Td>
                      <Td right>{EGP(a.total)}</Td>
                      <Td><StatusBadge label={a.status} /></Td>
                    </tr>
                  ))
                )}
              </tbody>
            </TableWrap>
          </Section>
        )}

        {/* ════════════════════════════════════════════════
            Departures
        ════════════════════════════════════════════════ */}
        {data && (
          <Section title="Departures" count={data.departures.length} accent="#f59e0b">
            <TableWrap>
              <thead>
                <tr>
                  <Th>Booking ID</Th>
                  <Th>Guest</Th>
                  <Th>Room</Th>
                  <Th>Nights</Th>
                  <Th right>Total</Th>
                  <Th>Payment Status</Th>
                </tr>
              </thead>
              <tbody>
                {data.departures.length === 0 ? (
                  <EmptyRow cols={6} msg="No departures for this date" />
                ) : (
                  data.departures.map((d) => (
                    <tr key={d.bookingId}>
                      <Td muted>{d.bookingId}</Td>
                      <Td>{d.guestName}</Td>
                      <Td>{d.roomId}</Td>
                      <Td muted>{d.nights}</Td>
                      <Td right>{EGP(d.total)}</Td>
                      <Td><StatusBadge label={d.paymentStatus} /></Td>
                    </tr>
                  ))
                )}
              </tbody>
            </TableWrap>
          </Section>
        )}

        {/* ════════════════════════════════════════════════
            In House
        ════════════════════════════════════════════════ */}
        {data && (
          <Section title="In House" count={data.inHouse.length} accent="#10b981">
            <TableWrap>
              <thead>
                <tr>
                  <Th>Guest</Th>
                  <Th>Room</Th>
                  <Th>Nights</Th>
                  <Th>Check-out</Th>
                  <Th right>Paid</Th>
                  <Th right>Remaining</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {data.inHouse.length === 0 ? (
                  <EmptyRow cols={7} msg="No in-house guests" />
                ) : (
                  data.inHouse.map((g) => (
                    <tr key={g.bookingId}>
                      <Td>{g.guestName}</Td>
                      <Td>{g.roomId}</Td>
                      <Td muted>{g.nights}</Td>
                      <Td muted>{fmtDate(g.checkOut)}</Td>
                      <Td right>
                        <span style={{ color: "#10b981" }}>{EGP(g.paid)}</span>
                      </Td>
                      <Td right>
                        <span style={{ color: g.remaining > 0 ? "#ef4444" : "#64748b" }}>
                          {EGP(g.remaining)}
                        </span>
                      </Td>
                      <Td><StatusBadge label={g.paymentStatus} /></Td>
                    </tr>
                  ))
                )}
              </tbody>
            </TableWrap>
          </Section>
        )}

        {/* ════════════════════════════════════════════════
            Shift Settlement
        ════════════════════════════════════════════════ */}
        {data && s && (
          <div style={{
            background: "#0E1E38",
            border: "1px solid rgba(201,168,76,0.25)",
            borderRadius: 14,
            marginBottom: 20,
            overflow: "hidden",
          }}>
            {/* Header */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 22px",
              borderBottom: "1px solid rgba(201,168,76,0.15)",
              flexWrap: "wrap",
              gap: 12,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ width: 4, height: 22, borderRadius: 4, background: "#c9a84c", display: "inline-block" }} />
                <span style={{ fontSize: 16, fontWeight: 700, color: "#F2EBD9" }}>Shift Settlement</span>
                <span style={{
                  fontSize: 12, fontWeight: 600, color: "#c9a84c",
                  background: "rgba(201,168,76,0.12)", border: "1px solid rgba(201,168,76,0.3)",
                  borderRadius: 8, padding: "1px 8px",
                }}>
                  {data.payments.length} payments
                </span>
              </div>
              <div className="na-no-print" style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => printSettlement("summary", data, localStorage.getItem("ai2_hotel") || "Hotel")}
                  style={{
                    background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.3)",
                    borderRadius: 8, padding: "7px 14px", color: "#10b981",
                    fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                    display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  <FontAwesomeIcon icon={faPrint} /> Print Summary
                </button>
                <button
                  onClick={() => printSettlement("transactions", data, localStorage.getItem("ai2_hotel") || "Hotel")}
                  style={{
                    background: "rgba(201,168,76,0.12)", border: "1px solid rgba(201,168,76,0.3)",
                    borderRadius: 8, padding: "7px 14px", color: "#c9a84c",
                    fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
                    display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  <FontAwesomeIcon icon={faPrint} /> Print Transactions
                </button>
              </div>
            </div>

            {/* Summary cards */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: 14,
              padding: "18px 22px 14px",
            }}>
              {[
                { label: "Cash", count: s.cashCount, total: s.cashCollected, color: "#10b981", icon: faMoneyBillWave },
                { label: "Visa / Card", count: s.visaCount, total: s.visaCollected, color: "#c9a84c", icon: faCreditCard },
                { label: "Instapay", count: s.instapayCount, total: s.instapayCollected, color: "#a855f7", icon: faMobileScreen },
              ].map(k => (
                <div key={k.label} style={{
                  background: "#111E35",
                  border: `1px solid ${k.color}25`,
                  borderRadius: 12,
                  padding: "14px 16px",
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      {k.label}
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: k.color,
                      background: k.color + "1a", border: `1px solid ${k.color}44`,
                      borderRadius: 20, padding: "1px 8px",
                    }}>
                      {k.count} tx
                    </span>
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>
                    {EGP_INT(k.total)}
                  </div>
                </div>
              ))}
              <div style={{
                background: "rgba(201,168,76,0.06)",
                border: "1px solid rgba(201,168,76,0.2)",
                borderRadius: 12,
                padding: "14px 16px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}>
                <div style={{ fontSize: 11, color: "rgba(242,235,217,0.4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                  Grand Total
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#F2EBD9" }}>
                  {EGP_INT(s.totalPaymentsCollected)}
                </div>
                <div style={{ fontSize: 11, color: "rgba(242,235,217,0.35)", marginTop: 4 }}>
                  {data.payments.length} transaction{data.payments.length !== 1 ? "s" : ""}
                </div>
              </div>
            </div>

            {/* Transaction list */}
            <div style={{ padding: "0 22px 20px" }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: "rgba(242,235,217,0.35)",
                textTransform: "uppercase", letterSpacing: "0.6px",
                marginBottom: 10, marginTop: 4,
              }}>
                Transaction Log
              </div>
              {data.payments.length === 0 ? (
                <div style={{ textAlign: "center", padding: "28px 0", color: "rgba(242,235,217,0.35)", fontSize: 13 }}>
                  No payments recorded for this date
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {data.payments.map((p, i) => {
                    const mc = methodColor2(p.method);
                    const mLabel = methodLabel(p.method, p.cardLast4);
                    return (
                      <div key={p.id} style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                        borderRadius: 8,
                        padding: "10px 12px",
                        flexWrap: "wrap",
                      }}>
                        <span style={{ fontSize: 11, color: "rgba(242,235,217,0.3)", minWidth: 20, textAlign: "right" }}>
                          {i + 1}
                        </span>
                        <span style={{ fontSize: 11, color: "rgba(242,235,217,0.35)", minWidth: 42 }}>
                          {fmtTime(p.createdAt)}
                        </span>
                        <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: "#F2EBD9", minWidth: 120 }}>
                          {p.guestName || <span style={{ color: "rgba(242,235,217,0.35)", fontWeight: 400, fontSize: 12 }}>{p.bookingId}</span>}
                        </span>
                        <span style={{ fontSize: 15, fontWeight: 800, color: "#10b981", minWidth: 110, textAlign: "right" }}>
                          {EGP_INT(p.amount)}
                        </span>
                        <span style={{
                          fontSize: 12, fontWeight: 600,
                          color: mc.color, background: mc.bg,
                          border: `1px solid ${mc.border}`,
                          borderRadius: 8, padding: "3px 10px",
                          whiteSpace: "nowrap",
                        }}>
                          {mLabel}
                        </span>
                        {p.note && (
                          <span style={{ fontSize: 11, color: "rgba(242,235,217,0.35)", fontStyle: "italic", flex: 1 }}>
                            {p.note}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Total footer bar */}
            {data.payments.length > 0 && (
              <div style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                padding: "12px 22px",
                borderTop: "1px solid rgba(201,168,76,0.15)",
                gap: 10,
              }}>
                <span style={{ color: "rgba(242,235,217,0.35)", fontSize: 13 }}>Total collected:</span>
                <span style={{ color: "#10b981", fontWeight: 900, fontSize: 17 }}>
                  {EGP_INT(data.payments.reduce((a, p) => a + p.amount, 0))}
                </span>
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════
            New Bookings (bonus section)
        ════════════════════════════════════════════════ */}
        {data && data.newBookings.length > 0 && (
          <Section title="New Bookings Today" count={data.newBookings.length} accent="#a855f7">
            <TableWrap>
              <thead>
                <tr>
                  <Th>Booking ID</Th>
                  <Th>Guest</Th>
                  <Th>Room</Th>
                  <Th>Check-in</Th>
                  <Th>Check-out</Th>
                  <Th right>Total</Th>
                  <Th>Source</Th>
                </tr>
              </thead>
              <tbody>
                {data.newBookings.map((b) => (
                  <tr key={b.bookingId}>
                    <Td muted>{b.bookingId}</Td>
                    <Td>{b.guestName}</Td>
                    <Td>{b.roomId}</Td>
                    <Td muted>{fmtDate(b.checkIn)}</Td>
                    <Td muted>{fmtDate(b.checkOut)}</Td>
                    <Td right>{EGP(b.total)}</Td>
                    <Td>
                      <span style={{
                        fontSize: 12, color: "#c9a84c",
                        background: "rgba(201,168,76,0.12)",
                        border: "1px solid #e2e8f0",
                        borderRadius: 8, padding: "2px 9px",
                        fontWeight: 600,
                      }}>
                        {b.source || "—"}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          </Section>
        )}

        {/* ════════════════════════════════════════════════
            Shift Notes
        ════════════════════════════════════════════════ */}
        <div className="na-no-print" style={{
          background: "#0E1E38",
          border: "1px solid #e2e8f0",
          borderRadius: 14,
          padding: "20px 22px",
          marginTop: 8,
        }}>
          <div style={{
            fontSize: 15, fontWeight: 700, color: "#F2EBD9", marginBottom: 12,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{
              width: 4, height: 20, borderRadius: 4,
              background: "#f59e0b", display: "inline-block",
            }} />
            Shift Handover Notes
          </div>
          <textarea
            value={shiftNotes}
            onChange={(e) => setShiftNotes(e.target.value)}
            placeholder="Add notes for the next shift — incidents, pending tasks, VIP arrivals, maintenance…"
            rows={5}
            style={{
              width: "100%",
              background: "#0E1E38",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              color: "#F2EBD9",
              fontSize: 14,
              padding: "12px 14px",
              resize: "vertical",
              outline: "none",
              fontFamily: "inherit",
              lineHeight: 1.6,
              boxSizing: "border-box",
            }}
          />
          <p style={{ margin: "8px 0 0", fontSize: 12, color: "rgba(242,235,217,0.45)" }}>
            These notes are for the current session only and are not saved to the server.
          </p>
        </div>

      </div>
    </DashboardLayout>
  );
}
